import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FileText,
  X,
  Loader2,
  ArrowLeft,
  Briefcase,
  Building2,
  Receipt,
  ChevronDown,
  MessageSquare,
} from "lucide-react";
import { invoicesApi, taxSettingsApi } from "../../services/api";
import {
  TaxConfig,
  DEFAULT_TAX_CONFIG,
  calculateTaxCents,
  getTaxLabel,
} from "../../utils/taxCalculation";

// ── Line item types ──────────────────────────────────────────────

type LineKind = "fee" | "disbursement" | "expense";

interface LineItem {
  kind: LineKind;
  description: string;
  note: string;
  quantity: number;
  unitPriceDollars: string;
  taxable: boolean;
}

const KIND_CONFIG: Record<
  LineKind,
  {
    label: string;
    shortLabel: string;
    icon: typeof Briefcase;
    defaultTaxable: boolean;
    badgeBg: string;
    badgeText: string;
  }
> = {
  fee: {
    label: "Professional Fee",
    shortLabel: "Prof. Fee",
    icon: Briefcase,
    defaultTaxable: true,
    badgeBg: "bg-emerald-50",
    badgeText: "text-emerald-700",
  },
  disbursement: {
    label: "Disbursement",
    shortLabel: "Disbursmt",
    icon: Building2,
    defaultTaxable: false,
    badgeBg: "bg-slate-100",
    badgeText: "text-slate-600",
  },
  expense: {
    label: "Expense",
    shortLabel: "Expense",
    icon: Receipt,
    defaultTaxable: true,
    badgeBg: "bg-amber-50",
    badgeText: "text-amber-700",
  },
};

const emptyLine = (): LineItem => ({
  kind: "fee",
  description: "",
  note: "",
  quantity: 1,
  unitPriceDollars: "",
  taxable: true,
});

interface InvoiceSubPanelProps {
  matterId: string;
  clientId: string;
  clientName?: string;
  matterTitle: string;
  matterNumber?: string;
  onCreated?: (invoiceId: string) => void;
  onClose: () => void;
}

const formatCurrency = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

// ── Kind selector dropdown ───────────────────────────────────────

const KindSelector: React.FC<{
  value: LineKind;
  onChange: (kind: LineKind) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const cfg = KIND_CONFIG[value];
  const Icon = cfg.icon;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${cfg.badgeBg} ${cfg.badgeText}`}
      >
        <Icon className="h-3 w-3" />
        {cfg.shortLabel}
        <ChevronDown className="h-3 w-3 opacity-50" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-20 w-48">
          {(Object.keys(KIND_CONFIG) as LineKind[]).map((k) => {
            const c = KIND_CONFIG[k];
            const KIcon = c.icon;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  onChange(k);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                  k === value ? "font-medium" : "text-slate-600"
                }`}
              >
                <KIcon className={`h-3.5 w-3.5 ${c.badgeText}`} />
                {c.label}
                {!c.defaultTaxable && (
                  <span className="text-[10px] text-slate-400 ml-auto">
                    No tax
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────

const InvoiceSubPanel: React.FC<InvoiceSubPanelProps> = ({
  matterId,
  clientId,
  clientName,
  matterTitle,
  matterNumber,
  onCreated,
  onClose,
}) => {
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);
  const [dueAt, setDueAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  });
  const [lineItems, setLineItems] = useState<LineItem[]>([emptyLine()]);
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  // Load firm tax settings
  useEffect(() => {
    taxSettingsApi
      .get()
      .then((res) => {
        const d = res.data?.data || res.data;
        if (d?.taxMode) {
          setTaxConfig({
            taxMode: d.taxMode,
            hstRate: d.hstRate ?? 13,
            gstRate: d.gstRate ?? 5,
            pstRate: d.pstRate ?? 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  const updateLine = (idx: number, updates: Partial<LineItem>) => {
    setLineItems((prev) =>
      prev.map((li, i) => (i === idx ? { ...li, ...updates } : li))
    );
  };

  const handleKindChange = (idx: number, kind: LineKind) => {
    updateLine(idx, {
      kind,
      taxable: KIND_CONFIG[kind].defaultTaxable,
    });
  };

  const removeLine = (idx: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
    setExpandedNotes((prev) => {
      const next = new Set<number>();
      prev.forEach((n) => {
        if (n < idx) next.add(n);
        else if (n > idx) next.add(n - 1);
      });
      return next;
    });
  };

  const addLine = () => setLineItems((prev) => [...prev, emptyLine()]);

  const toggleNote = (idx: number) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // ── Totals calculations ────────────────────────────────────────

  const getLineCents = (li: LineItem) =>
    Math.round(parseFloat(li.unitPriceDollars || "0") * li.quantity * 100);

  const validItems = useMemo(
    () =>
      lineItems.filter(
        (li) =>
          li.description.trim() && parseFloat(li.unitPriceDollars || "0") > 0
      ),
    [lineItems]
  );

  const taxableSubtotal = useMemo(
    () => validItems.filter((li) => li.taxable).reduce((s, li) => s + getLineCents(li), 0),
    [validItems]
  );

  const nonTaxableSubtotal = useMemo(
    () =>
      validItems.filter((li) => !li.taxable).reduce((s, li) => s + getLineCents(li), 0),
    [validItems]
  );

  const subtotal = taxableSubtotal + nonTaxableSubtotal;

  const taxTotal = useMemo(() => {
    if (taxConfig.taxMode === "none") return 0;
    return validItems
      .filter((li) => li.taxable)
      .reduce((sum, li) => {
        const { totalTaxCents } = calculateTaxCents(getLineCents(li), taxConfig);
        return sum + totalTaxCents;
      }, 0);
  }, [validItems, taxConfig]);

  const grandTotal = subtotal + taxTotal;

  const hasValidItems = validItems.length > 0;

  // ── Submit ─────────────────────────────────────────────────────

  const handleCreate = async () => {
    setCreating(true);
    setError("");
    try {
      const apiLineItems = validItems.map((li) => {
        const unitPriceCents = Math.round(
          parseFloat(li.unitPriceDollars) * 100
        );
        const amountCents = unitPriceCents * li.quantity;
        const { totalTaxCents } = li.taxable
          ? calculateTaxCents(amountCents, taxConfig)
          : { totalTaxCents: 0 };
        const desc = li.note.trim()
          ? `${li.description.trim()} — ${li.note.trim()}`
          : li.description.trim();
        return {
          kind: li.kind,
          description: desc,
          quantity: li.quantity,
          unitPriceCents,
          amountCents,
          taxable: li.taxable,
          taxAmountCents: totalTaxCents,
        };
      });

      const res = await invoicesApi.create({
        matterId,
        clientId,
        lineItems: apiLineItems,
        dueAt,
        notesToClient: notes.trim() || null,
      });

      const invoiceId = res.data?._id;

      if (invoiceId) {
        try {
          await invoicesApi.send(invoiceId);
        } catch {
          // Invoice created but send failed — still a success
        }
      }

      onCreated?.(invoiceId);
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.error ||
        data?.msg ||
        data?.message ||
        "Failed to create invoice";
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const taxLabel = getTaxLabel(taxConfig);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white shrink-0">
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors md:hidden"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
          <FileText className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-800">Quick Invoice</h3>
          <p className="text-xs text-slate-500 truncate">
            {matterNumber ? `${matterNumber} — ${matterTitle}` : matterTitle}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        {/* Client + Matter info */}
        {clientName && (
          <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">Client</p>
              <p className="text-sm font-medium text-slate-700">
                {clientName}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Matter</p>
              <p className="text-sm font-medium text-slate-700">
                {matterNumber || matterTitle}
              </p>
            </div>
          </div>
        )}

        {/* Due Date */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Due Date *
          </label>
          <input
            type="date"
            required
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
          />
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-slate-700">
              Line Items *
            </label>
            <button
              type="button"
              onClick={addLine}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={{
                color: "var(--color-primary)",
                backgroundColor:
                  "color-mix(in srgb, var(--color-primary) 10%, transparent)",
              }}
            >
              + Add Item
            </button>
          </div>

          {lineItems.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl">
              No line items yet. Click &quot;+ Add Item&quot; to start.
            </p>
          ) : (
            <div className="space-y-3">
              {lineItems.map((li, i) => {
                const noteExpanded =
                  expandedNotes.has(i) || li.note.length > 0;
                return (
                  <div key={i} className="bg-slate-50 rounded-xl p-4">
                    {/* Kind selector + Description + Remove */}
                    <div className="flex items-start gap-2 mb-2">
                      <KindSelector
                        value={li.kind}
                        onChange={(k) => handleKindChange(i, k)}
                      />
                      <input
                        type="text"
                        placeholder="Description *"
                        value={li.description}
                        onChange={(e) =>
                          updateLine(i, { description: e.target.value })
                        }
                        autoFocus={i === lineItems.length - 1}
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                      {lineItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLine(i)}
                          className="p-1.5 rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Expandable note */}
                    {!noteExpanded ? (
                      <button
                        type="button"
                        onClick={() => toggleNote(i)}
                        className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors mb-2 ml-0.5"
                      >
                        <MessageSquare className="h-3 w-3" />
                        + Note
                      </button>
                    ) : (
                      <div className="mb-2">
                        <textarea
                          value={li.note}
                          onChange={(e) =>
                            updateLine(i, { note: e.target.value })
                          }
                          onBlur={() => {
                            if (!li.note.trim()) {
                              setExpandedNotes((prev) => {
                                const next = new Set(prev);
                                next.delete(i);
                                return next;
                              });
                            }
                          }}
                          rows={2}
                          placeholder="Additional details..."
                          autoFocus
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:outline-none focus:border-emerald-600 resize-none"
                        />
                      </div>
                    )}

                    {/* Qty + Price + Tax + Amount */}
                    <div className="flex items-center gap-3">
                      <div className="w-16">
                        <label className="block text-[10px] font-medium text-slate-400 mb-0.5">
                          Qty
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={li.quantity}
                          onChange={(e) =>
                            updateLine(i, {
                              quantity: parseInt(e.target.value) || 1,
                            })
                          }
                          className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-[10px] font-medium text-slate-400 mb-0.5">
                          Unit Price
                        </label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                            $
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={li.unitPriceDollars}
                            onChange={(e) =>
                              updateLine(i, {
                                unitPriceDollars: e.target.value,
                              })
                            }
                            className="w-full rounded-lg border border-slate-200 pl-7 pr-3 py-1.5 text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex items-end pb-0.5">
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={li.taxable}
                            onChange={(e) =>
                              updateLine(i, { taxable: e.target.checked })
                            }
                            className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-[11px] text-slate-500">
                            Tax
                          </span>
                        </label>
                      </div>
                      <div className="text-right min-w-[72px]">
                        <label className="block text-[10px] font-medium text-slate-400 mb-0.5">
                          Amount
                        </label>
                        <p className="text-sm font-medium text-slate-700 py-1.5">
                          {formatCurrency(getLineCents(li))}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Notes to Client
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
            placeholder="Any notes for the client..."
          />
        </div>

        {/* ── Totals ─────────────────────────────────────── */}
        {hasValidItems && (
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            {/* Taxable subtotal */}
            {taxableSubtotal > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Fees &amp; Expenses</span>
                <span className="text-slate-700 font-medium">
                  {formatCurrency(taxableSubtotal)}
                </span>
              </div>
            )}

            {/* Tax line */}
            {taxConfig.taxMode !== "none" && taxTotal > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">{taxLabel}</span>
                <span className="text-slate-700 font-medium">
                  {formatCurrency(taxTotal)}
                </span>
              </div>
            )}

            {/* Non-taxable disbursements */}
            {nonTaxableSubtotal > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">
                  Disbursements{" "}
                  <span className="text-[10px] text-slate-400">
                    (non-taxable)
                  </span>
                </span>
                <span className="text-slate-700 font-medium">
                  {formatCurrency(nonTaxableSubtotal)}
                </span>
              </div>
            )}

            {/* Grand total */}
            <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2">
              <span className="text-slate-800">Total</span>
              <span style={{ color: "var(--color-primary)" }}>
                {formatCurrency(grandTotal)}
              </span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={creating || !hasValidItems}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => {
            if (!creating)
              e.currentTarget.style.backgroundColor =
                "var(--color-primary-hover)";
          }}
          onMouseLeave={(e) => {
            if (!creating)
              e.currentTarget.style.backgroundColor = "var(--color-primary)";
          }}
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {creating ? "Sending..." : "Create & Send"}
        </button>
      </div>
    </div>
  );
};

export default InvoiceSubPanel;
